import { FileInfo, SpaceLinqFile } from "@/types/message";

export const MAX_FILE_SIZE = 1024 * 1024;
export const WARN_FILE_SIZE = 12 * 1024;
const QUALITY_START = 0.8;
const QUALITY_LOW = 0.4;
const RESIZE_FACTOR = 0.8;
const MAX_RETRIES = 5;

export function isValidFileType(file: File | null) {
  if (file === null) {
    return false;
  }

  const supportedMimeTypes = [
    'application/pdf',
    'application/json',
    'application/octet-stream',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const isTextFile = file.type.startsWith("text");
  const isImageFile = file.type.startsWith("image");
  const isSupportedFile = supportedMimeTypes.includes(file.type);

  return isTextFile || isImageFile || isSupportedFile;
}

/**
 * Converts an image file to the webp format, adjusting its size and quality until
 * it meets the size constraints or runs out of retries.
 * 
 * @param {File} image - The image file to be converted.
 * @returns {Promise<File>} - A Promise that resolves to the webp file if successful.
 * @throws {Error} - Throws an error if the image cannot be converted within the size and quality constraints.
 */
export async function convertToWebp(image: File) {
  if (image.size <= WARN_FILE_SIZE) {
    return image;
  }

  let smallestWebp: File | null = null;

  try {
    const img = await loadImageFromFile(image);

    let quality = QUALITY_START;
    let retries = MAX_RETRIES;
    let width = img.width;
    let height = img.height;

    // adjust size + quality until we're under the size limit or run out of retries
    while (retries > 0) {
      const canvas = drawImageOnCanvas(img, width, height);

      let blob: Blob | null = null;
      try {
        blob = await createWebpBlob(canvas, quality);
      } catch {
        // WebP encoding failed (unsupported browser, memory, etc.) — try next iteration
      }

      if (blob) {
        const convertedFile = new File(
          [blob],
          `${image.name.split(".").slice(0, -1).join(".")}.webp`,
          { type: "image/webp" }
        );

        if (convertedFile.size <= WARN_FILE_SIZE) {
          return convertedFile;
        } else if (!smallestWebp || convertedFile.size < smallestWebp.size) {
          smallestWebp = convertedFile;
        }
      }

      // reduce quality, or resize image to be smaller and try again
      if (quality > QUALITY_LOW) {
        quality -= 0.1;
      } else {
        width = Math.floor(width * RESIZE_FACTOR);
        height = Math.floor(height * RESIZE_FACTOR);
        quality = QUALITY_START;
        retries--;
      }
    }
  } catch {
    // loadImageFromFile or drawImageOnCanvas failed — fall through to size-based fallback
  }

  if (smallestWebp && smallestWebp.size <= MAX_FILE_SIZE) {
    return smallestWebp;
  }

  if (image.size <= MAX_FILE_SIZE) {
    return image;
  }

  throw new Error(
    "Failed to convert image to webp within size and quality constraints."
  );
}

/**
 * Loads an image from a File object and returns it as an HTMLImageElement.
 * 
 * @param {File} image - The image file to be loaded.
 * @returns {Promise<HTMLImageElement>} - A Promise that resolves to the loaded HTMLImageElement.
 * @throws {Error} - Throws an error if the image fails to load or be read.
 */
async function loadImageFromFile(image: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load the image"));
    };

    reader.onerror = () => reject(new Error("Failed to read the image file"));
    reader.readAsDataURL(image);
  });
}

/**
 * Draws an image onto a canvas element.
 * 
 * @param {HTMLImageElement} img - The image to be drawn onto the canvas.
 * @param {number} width - The width of the canvas.
 * @param {number} height - The height of the canvas.
 * @returns {HTMLCanvasElement} - The canvas element with the image drawn on it.
 * @throws {Error} - Throws an error if the canvas context cannot be obtained.
 */
function drawImageOnCanvas(
  img: HTMLImageElement,
  width: number,
  height: number
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  
  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  ctx.drawImage(img, 0, 0, width, height);

  return canvas;
}

/**
 * Creates a webp-encoded blob from a canvas element at a specified quality.
 * 
 * @param {HTMLCanvasElement} canvas - The canvas element containing the image.
 * @param {number} quality - The quality of the image, a decimal in the range [0,1].
 * @returns {Promise<Blob | null>} - A Promise that resolves to the webp blob if successful.
 * @throws {Error} - Throws an error if the blob could not be created.
 */
async function createWebpBlob(
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create WebP blob"));
        }
      },
      "image/webp",
      quality
    );
  });
}

/**
 * Matches the type of a file to the correct reader and reads the data using the provided blob
 * @param {File.type} type
 * @param {File} file
 * @param {Reader} reader
 */
export function readFileContent(type: string, file: File, reader: FileReader) {
  if (type.startsWith('image')) {
    reader.readAsDataURL(file);
    return;
  }

  switch (type) {
    case 'application/pdf':
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      reader.readAsDataURL(file);
      break;
    default:
      reader.readAsDataURL(file);
      break;
  }
}

/**
 * Reads the provided file as a base64-encoded string along with its metadata.
 *
 * @param {SpaceLinqFile} file - The file to be read.
 * @returns {Promise<{ base64: string; name: string; type: number }>}
 *          A promise that resolves to an object containing the base64 encoded content,
 *          file name, and media type (11 for images, 12 for others).
 * @throws {Error} Throws an error if the file reading fails.
 */
async function readFileAsBase64(
  file: SpaceLinqFile,
): Promise<{ media: string; name: string; filetype: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64Content = reader.result.split(",")[1];
        const media = reader.result.startsWith("data:") ? base64Content : btoa(reader.result);
        const filetype = file.type.startsWith("image") ? 11 : 12;

        resolve({ media, name: file.name, filetype });
      } else {
        reject(new Error("FileReader result is not a string."));
      }
    };

    reader.onerror = (error) => reject(error);

    readFileContent(file.type, file, reader);
  });
}

/**
 * Formats the provided file and message into a JSON string. The JSON includes 
 * the media type, file size (in KB), media content, message, and filename.
 *
 * @param {SpaceLinqFile} file - The file to be processed and included in the JSON object.
 * @param {string} caption - The file caption, stored in the JSON as the message.
 * @returns {Promise<FileInfo>} A promise that resolves to the JSON containing the file's details and message.
 * @throws {Error} Throws an error if the file reading process fails.
 */
export async function formatFileToJson(
  file: SpaceLinqFile,
  caption: string,
): Promise<FileInfo> {
  const { media, name, filetype } = await readFileAsBase64(file);

  const jsonObj = {
    cls: "mms",
    dtype: filetype,
    dkb: file.size / 1000,
    media: media,
    msg: caption,
    fname: name
  };

  return jsonObj;
}

/**
 * Formats a file size in kilobytes (dkb) to a human-readable string with units.
 *
 * @param {number} dkb - The file size in kilobytes.
 * @returns A string representing the file size in bytes (B), kilobytes (KB),
 * megabytes (MB), or gigabytes (GB), depending on the size of the file.
 */

export function formatFileSize(dkb: number): string {
  const filesize = dkb * 1000;
  if (filesize < 1024) {
    return `${filesize} B`;
  } else if (filesize < 1024 * 1024) {
    return `${(filesize / 1024).toFixed(2)} KB`;
  } else if (filesize < 1024 * 1024 * 1024) {
    return `${(filesize / (1024 * 1024)).toFixed(2)} MB`;
  } else {
    return `${(filesize / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
}
